import { z } from 'zod'
import { PUBLIC_WORK_WHERE_SQL } from './public-work'
import { json } from './http'
import type { Bindings } from '../bindings'
import {
  communityProject,
  communityProjects,
  projectRepositories,
  projectRepository,
} from '@/lib/community-projects'

/** Public repository catalog; inclusion does not confer upstream maintainer authority. */
export function readProjects() {
  return json({
    projects: communityProjects.map((project) => {
      const repository = projectRepository(project)
      const contributionRepository = project.upstream
        ? `https://github.com/${project.upstream}`
        : repository
      return {
        slug: project.slug,
        name: project.name,
        description: project.en,
        repository,
        upstream: project.upstream ? contributionRepository : null,
        page: `https://www.horizon-tech.io/en-US/community/projects/${project.slug}`,
        contributionGuide: `${contributionRepository}/blob/main/CONTRIBUTING.md`,
        licence: `${repository}/blob/main/${project.licencePath}`,
        workEndpoint: `https://www.horizon-tech.io/api/agents/projects/${project.slug}/work`,
      }
    }),
  })
}

export const projectTaskSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  kind: z.string(),
  status: z.enum(['open', 'closed']),
  createdAt: z.string(),
  repo: z.string().url(),
  revision: z.string(),
})
export const projectWorkSchema = z.object({
  project: z.string(),
  items: z.array(projectTaskSchema).max(30),
  nextCursor: z.string().uuid().nullable(),
})
export type ProjectWork = z.infer<typeof projectWorkSchema>
const repositorySql =
  "rtrim(lower(json_extract(CASE WHEN json_valid(i.base_ref) THEN i.base_ref ELSE '{}' END,'$.repo')),'/')"
const predicate = `${PUBLIC_WORK_WHERE_SQL} AND ${repositorySql} IN (SELECT value FROM json_each(?))`
export const PROJECT_ANCHOR_SQL = `SELECT i.created_at AS createdAt FROM work_items i JOIN agent_workspaces w ON w.id=i.workspace_id WHERE ${predicate} AND i.id=?`
export const projectWorkSql = (hasCursor: boolean) =>
  `SELECT i.id,i.title,i.kind,i.status,i.created_at AS createdAt,json_extract(i.base_ref,'$.repo') AS repo,json_extract(i.base_ref,'$.revision') AS revision FROM work_items i JOIN agent_workspaces w ON w.id=i.workspace_id WHERE ${predicate} ${hasCursor ? 'AND (i.created_at<? OR (i.created_at=? AND i.id<?))' : ''} ORDER BY i.created_at DESC,i.id DESC LIMIT 31`
export function projectWorkView(
  slug: string,
  rows: Array<z.infer<typeof projectTaskSchema>>,
): ProjectWork {
  const items = rows.slice(0, 30)
  return projectWorkSchema.parse({
    project: slug,
    items,
    nextCursor: rows.length > 30 ? items.at(-1)!.id : null,
  })
}
export async function readProjectWork(
  request: Request,
  env: Bindings,
  slug: string,
) {
  const project = communityProject(slug)
  if (!project) return json({ error: 'Project not found' }, 404)
  const cursor = new URL(request.url).searchParams.get('cursor')
  if (cursor && !z.string().uuid().safeParse(cursor).success)
    return json({ error: 'Invalid cursor' }, 400)
  const repositories = JSON.stringify(projectRepositories(project))
  const anchor = cursor
    ? await env.DB.prepare(PROJECT_ANCHOR_SQL)
        .bind(repositories, cursor)
        .first<{ createdAt: string }>()
    : null
  if (cursor && !anchor) return json({ error: 'Cursor not found' }, 404)
  const rows = (
    await env.DB.prepare(projectWorkSql(!!anchor))
      .bind(
        repositories,
        ...(anchor ? [anchor.createdAt, anchor.createdAt, cursor] : []),
      )
      .all<z.infer<typeof projectTaskSchema>>()
  ).results
  return json(projectWorkView(slug, rows))
}
